import {defineStore} from 'pinia'
import {ref} from 'vue'
import {$$, $computed, $ref} from '@vue-macros/reactivity-transform/macros'
import {IParticularColor} from '@toolify/server/src/services/ColorService/types/IParticularColor'
import {IResourceAccessSummary} from '@toolify/server/src/services/PermissionService/types/IResourceAccessSummary'
import {
  IResourceItemResponseInstance,
} from '@toolify/server/src/adapters/InstanceApiResponseAdapter/types/responseInstances/IResourceItemResponseInstance'
import {
  IResourceGroupResponseInstance,
} from '@toolify/server/src/adapters/InstanceApiResponseAdapter/types/responseInstances/IResourceGroupResponseInstance'
import {useWorkspaceStore} from '@toolify/client/src/stores/WorkspaceStore/useWorkspaceStore'
import {useResourceItemApiStore} from '@toolify/client/src/stores/api/ResourceItemApiStore/useResourceItemApiStore'
import {useResourceGroupApiStore} from '@toolify/client/src/stores/api/ResourceGroupApiStore/useResourceGroupApiStore'
import {useActivityItemSocketListener} from '@toolify/client/src/composables/useActivityItemSocketListener'
import {ActivityItemType} from '@toolify/server/src/models/mongoose/ActivityItemModel/enum/ActivityItemType'
import {useSocketListener} from '@toolify/client/src/composables/useSocketListener'
import {
  IResourceLastActivityChangeEmitPayload,
} from '@toolify/server/src/services/SocketService/strategies/GlobalSocketEventTypeStrategy/IResourceLastActivityChangeEmitPayload'
import {
  IResourceSpaceDeletedEmitPayload,
} from '@toolify/server/src/services/SocketService/strategies/GlobalSocketEventTypeStrategy/IResourceSpaceDeletedEmitPayload'
import {GlobalSocketEventType} from '@toolify/server/src/services/SocketService/enum/GlobalSocketEventType'
import {getDatabaseViewResourceItemPatch} from '@toolify/client/src/stores/ResourceItemStore/functions/getDatabaseViewResourceItemPatch'
import {
  IResourceReadAtChangeEmitPayload,
} from '@toolify/server/src/services/SocketService/strategies/GlobalSocketEventTypeStrategy/IResourceReadAtChangeEmitPayload'
import {
  INotificationCreatedEmitPayload,
} from '@toolify/server/src/services/SocketService/strategies/GlobalSocketEventTypeStrategy/INotificationCreatedEmitPayload'
import {
  INotificationsSeenEmitPayload,
} from '@toolify/server/src/services/SocketService/strategies/GlobalSocketEventTypeStrategy/INotificationsSeenEmitPayload'
import {
  InotificationReadEventPayload,
} from '@toolify/server/src/services/SocketService/strategies/GlobalSocketEventTypeStrategy/INotificationReadEmitPayload'
import {
  INotificationsReadEmitPayload,
} from '@toolify/server/src/services/SocketService/strategies/GlobalSocketEventTypeStrategy/INotificationsReadEmitPayload'
import {
  INotificationResourcePathEmitItem,
} from '@toolify/server/src/services/SocketService/strategies/GlobalSocketEventTypeStrategy/INotificationResourcePathEmitItem'
import {mapResourceItemResponseInstance} from '@toolify/client/src/modules/api/singletons/mapResourceItemResponseInstance'
import {ResourceType} from '@toolify/server/src/models/mongoose/PermissionModel/enum/ResourceType'
import {mapAutomationResponseInstance} from '@toolify/client/src/modules/api/singletons/mapAutomationResponseInstance'
import {IAutomationResponseInstance} from '@toolify/server/src/adapters/InstanceApiResponseAdapter/types/responseInstances/IAutomationResponseInstance'
import {IToJson} from '@toolify/client/src/types/IToJson'
import {useAuthStore} from '../AuthStore/useAuthStore'
import {IconNameType} from '@toolify/server/src/enum/IconNameType'
import {RouteLocationNormalizedLoaded, useRouter} from 'vue-router'
import {RouteName} from '@toolify/client/src/setup/router/enum/RouteName'
import {
  RouteParamTypeToResourceTypeMap,
} from '@toolify/client/src/setup/router/maps/RouteParamTypeToResourceTypeMap'
import {ApiException} from '@toolify/server/src/middleware/ApiResponseMiddleware/errors/ApiException'
import {KnownError} from '@toolify/server/src/middleware/ApiResponseMiddleware/types/KnownError'
import {useMessages} from '@toolify/client/src/modules/notifications/composables/useMessages'
import {i18n} from '@toolify/client/src/setup/i18n'
import {useCacheStore} from '@toolify/client/src/stores/CacheStore/useCacheStore'
import {
  IResourceAccessRevokedEmitPayload,
} from '@toolify/server/src/services/SocketService/strategies/GlobalSocketEventTypeStrategy/IResourceAccessRevokedEmitPayload'
import {useNotificationCountsStore} from '@toolify/client/src/stores/NotificationCountsStore/useNotificationCountsStore'

export const useResourceItemStore = defineStore('resourceItem', () => {
  const workspaceStore = useWorkspaceStore()
  const resourceItemApiStore = useResourceItemApiStore()
  const resourceGroupApiStore = useResourceGroupApiStore()
  const notificationCountsStore = useNotificationCountsStore()
  const authStore = useAuthStore()
  const router = useRouter()
  const messages = useMessages()
  const cacheStore = useCacheStore()
  
  const revokedAtByResourceKey = new Map<string, number>()
  let resourceItemsByWorkspaceIdMap = $ref<Map<string, IResourceItemResponseInstance[]>>(new Map())
  let resourceGroupsByWorkspaceIdMap = $ref<Map<string, IResourceGroupResponseInstance[]>>(new Map())
  let lastCreatedResourceItem = $ref<IResourceItemResponseInstance>(null)
  const isImportingTemplate = ref(false)

  function setIsImportingTemplate(value: boolean) {
    isImportingTemplate.value = value
  }

  let hasInitialized = $ref(false)
  function initialize() {
    hasInitialized = true
  }

  function dispose() {
    hasInitialized = false
  }

  const resourceItems = $computed(() => {
    const workspaceId = workspaceStore.currentWorkspace?.id
    if (!workspaceId) return undefined
    return resourceItemsByWorkspaceIdMap.get(workspaceId)
  })
  const sortedResourceItems = $computed(() => {
    if (!resourceItems) {
      return undefined
    }
    return [...resourceItems].sort((a, b) => {
      return a.position - b.position
    })
  })
  const resourceGroups = $computed(() => {
    const workspaceId = workspaceStore.currentWorkspace?.id
    if (!workspaceId) return undefined
    return resourceGroupsByWorkspaceIdMap.get(workspaceId)
  })
  const hasAnyResource = $computed(() => {
    return resourceItems?.length > 0
  })
  const hasLoaded = $computed(() => {
    const workspaceId = workspaceStore.currentWorkspace?.id
    if (!workspaceId) return false
    return resourceItemsByWorkspaceIdMap.has(workspaceId) && resourceGroupsByWorkspaceIdMap.has(workspaceId)
  })

  async function fetchResourceGroups(workspaceId: string) {
    try {
      const getResourceGroupsRes = await resourceGroupApiStore.getResourceGroups(workspaceId)
      setResourceGroupsByWorkspaceId(workspaceId, getResourceGroupsRes.resourceGroups)
    } catch (error) {
      if(error instanceof ApiException && error.name === KnownError.NoPermissions) {
        setResourceGroupsByWorkspaceId(workspaceId, [])
        return
      }
      throw error
    }
  }

  const pendingResourceGroupFetches = new Map<string, Promise<void>>()

  function fetchMissingResourceGroup(workspaceId: string, resourceGroupId: string | null) {
    const resourceGroups = resourceGroupsByWorkspaceIdMap.get(workspaceId)
    if(!resourceGroupId || !resourceGroups || resourceGroups.some(resourceGroup => resourceGroup.id === resourceGroupId) || pendingResourceGroupFetches.has(workspaceId)) {
      return
    }
    const pendingFetch = fetchResourceGroups(workspaceId)
      .catch(() => {})
      .finally(() => pendingResourceGroupFetches.delete(workspaceId))
    pendingResourceGroupFetches.set(workspaceId, pendingFetch)
  }
  async function fetchResourceItems(workspaceId: string) {
    const requestedAt = Date.now()
    const getResourceItemsRes = await resourceItemApiStore.getResourceItems(workspaceId)
    setResourceItemsByWorkspaceId(workspaceId, getResourceItemsRes.resourceItems, requestedAt)
  }
  function setResourceItemsByWorkspaceId(
    workspaceId: string,
    resourceItems: IResourceItemResponseInstance[],
    requestedAt = Date.now(),
  ) {
    const targetWorkspaceId = workspaceStore.currentWorkspace?.id
    if (!targetWorkspaceId) return
    const newResourceItemsByWorkspaceIdMap = new Map(resourceItemsByWorkspaceIdMap)
    newResourceItemsByWorkspaceIdMap.set(targetWorkspaceId, resourceItems.filter(resourceItem => {
      return getResourceItemRevokedAt(resourceItem) <= requestedAt
    }))
    resourceItemsByWorkspaceIdMap = newResourceItemsByWorkspaceIdMap
  }
  function getResourceKey(resourceType: ResourceType, resourceId: string) {
    return `${resourceType}:${resourceId}`
  }
  function isRouteOnRevokedResource(route: RouteLocationNormalizedLoaded, revokedResourceKeys: Set<string>) {
    const routeResource = route.meta.determineResource?.(route) ?? null
    if(routeResource && revokedResourceKeys.has(getResourceKey(routeResource.resourceType, routeResource.resourceId))) {
      return true
    }
    for(const [routeParamType, resourceType] of RouteParamTypeToResourceTypeMap) {
      const routeParam = route.params[routeParamType]
      const resourceIds = Array.isArray(routeParam) ? routeParam : [routeParam]
      if(resourceIds.some(resourceId => resourceId && revokedResourceKeys.has(getResourceKey(resourceType, resourceId)))) {
        return true
      }
    }
    return false
  }
  function getResourceItemRevokedAt(resourceItem: IResourceItemResponseInstance) {
    let revokedAt = 0
    for(const resourcePathItem of resourceItem.resourcePath ?? []) {
      const pathItemRevokedAt = revokedAtByResourceKey.get(
        getResourceKey(resourcePathItem.resourceType, String(resourcePathItem.resourceId)),
      )
      if(pathItemRevokedAt > revokedAt) {
        revokedAt = pathItemRevokedAt
      }
    }
    return revokedAt
  }
  function setResourceGroupsByWorkspaceId(workspaceId: string, resourceGroups: IResourceGroupResponseInstance[]) {
    const targetWorkspaceId = workspaceStore.currentWorkspace?.id
    if (!targetWorkspaceId) return
    const newResourceGroupsByWorkspaceIdMap = new Map(resourceGroupsByWorkspaceIdMap)
    newResourceGroupsByWorkspaceIdMap.set(targetWorkspaceId, resourceGroups)
    resourceGroupsByWorkspaceIdMap = newResourceGroupsByWorkspaceIdMap
  }
  function setResourceGroups(resourceGroups: IResourceGroupResponseInstance[]) {
    const workspaceId = workspaceStore.currentWorkspace?.id
    if(!workspaceId) {
      return
    }
    setResourceGroupsByWorkspaceId(workspaceId, resourceGroups)
  }
  function updateLocalResouceItems(newResourceItems: IResourceItemResponseInstance[]) {
    const newResourceItemsByWorkspaceIdMap = new Map(resourceItemsByWorkspaceIdMap)
    for (const [workspaceId, resourceItems] of newResourceItemsByWorkspaceIdMap.entries()) {
      newResourceItemsByWorkspaceIdMap.set(workspaceId, resourceItems.map(resourceItem => {
        const newResourceItem = newResourceItems.find(newResourceItem => newResourceItem.id === resourceItem.id)
        if(newResourceItem) {
          return {
            ...newResourceItem,
          }
        }
        return resourceItem
      }))
    }
    resourceItemsByWorkspaceIdMap = newResourceItemsByWorkspaceIdMap
  }

  function setResourceItems(resourceItems: IResourceItemResponseInstance[]) {
    const workspaceId = workspaceStore.currentWorkspace.id
    if(!workspaceId) {
      return
    }
    setResourceItemsByWorkspaceId(workspaceId, resourceItems)
  }
  async function markResourceAsRead(resourceType: ResourceType, resourceId: string) {
    await resourceItemApiStore.markResourceAsRead(resourceType, resourceId)
    // find this resource item in every workspace and update it
    const newResourceItemsByWorkspaceIdMap = new Map(resourceItemsByWorkspaceIdMap)
    for (const [workspaceId, resourceItems] of newResourceItemsByWorkspaceIdMap.entries()) {
      newResourceItemsByWorkspaceIdMap.set(workspaceId, resourceItems.map(resourceItem => {
        if(resourceItem.resourceEntity.resourceId === resourceId && resourceItem.resourceEntity.resourceType === resourceType) {
          return {
            ...resourceItem,
            readAt: new Date(),
          }
        }
        return resourceItem
      }))
    }
    resourceItemsByWorkspaceIdMap = newResourceItemsByWorkspaceIdMap
  }

  async function markResourceAsUnread(resourceType: ResourceType, resourceId: string) {
    await resourceItemApiStore.markResourceAsUnread(resourceType, resourceId)
    const newResourceItemsByWorkspaceIdMap = new Map(resourceItemsByWorkspaceIdMap)
    for (const [workspaceId, resourceItems] of newResourceItemsByWorkspaceIdMap.entries()) {
      newResourceItemsByWorkspaceIdMap.set(workspaceId, resourceItems.map(resourceItem => {
        if(resourceItem.resourceEntity.resourceId === resourceId && resourceItem.resourceEntity.resourceType === resourceType) {
          return {
            ...resourceItem,
            readAt: null,
          }
        }
        return resourceItem
      }))
    }
    resourceItemsByWorkspaceIdMap = newResourceItemsByWorkspaceIdMap
  }

  async function toggleResourceItemFavourite(resourceType: ResourceType, resourceId: string, isFavourite: boolean) {
    const {favouriteColor, favouritePosition} = await resourceItemApiStore.toggleResourceItemFavourite(resourceType, resourceId, isFavourite)
    const workspaceId = workspaceStore.currentWorkspace?.id
    const isListed = !!resourceItemsByWorkspaceIdMap.get(workspaceId)?.some(resourceItem => {
      return resourceItem.resourceEntity.resourceType === resourceType
        && resourceItem.resourceEntity.resourceId === resourceId
    })
    if(isFavourite && workspaceId && !isListed) {
      await fetchResourceItems(workspaceId)
      return
    }
    const newResourceItemsByWorkspaceIdMap = new Map(resourceItemsByWorkspaceIdMap)
    for(const [workspaceId, resourceItems] of newResourceItemsByWorkspaceIdMap.entries()) {
      if(!resourceItems) {
        continue
      }
      const toggledResourceItem = resourceItems.find(resourceItem => {
        return resourceItem.resourceEntity.resourceType === resourceType
          && resourceItem.resourceEntity.resourceId === resourceId
      })
      if(!toggledResourceItem) {
        continue
      }
      const wasFavourite = !!toggledResourceItem.isFavouriteToRequestingEntity
      const oldFavouritePosition = toggledResourceItem.favouritePosition ?? 0
      // Same positions as the server: a new favourite goes to the end, a removed
      // one closes its gap. Otherwise a reorder right after starring sends stale
      // positions
      newResourceItemsByWorkspaceIdMap.set(workspaceId, resourceItems.map(resourceItem => {
        if(resourceItem === toggledResourceItem) {
          return {
            ...resourceItem,
            isFavouriteToRequestingEntity: isFavourite,
            // The server stores a colour on every favourite
            ...(isFavourite ? {
              favouriteColor,
              favouritePosition,
            } : {}),
          }
        }
        if(!isFavourite && wasFavourite && resourceItem.isFavouriteToRequestingEntity
          && (resourceItem.favouritePosition ?? 0) > oldFavouritePosition) {
          return {
            ...resourceItem,
            favouritePosition: resourceItem.favouritePosition - 1,
          }
        }
        return resourceItem
      }))
    }
    resourceItemsByWorkspaceIdMap = newResourceItemsByWorkspaceIdMap
  }

  // Applied before the request: a favourite dragged in the sidebar needs its new
  // order in the same tick Sortable drops it, or Vue re-renders the old order over
  // the moved row. Moving shifts the favourites in between by one, like the server
  async function updateEntityResourceItem(resourceType: ResourceType, resourceId: string, position?: number, color?: IParticularColor) {
    const newResourceItemsByWorkspaceIdMap = new Map(resourceItemsByWorkspaceIdMap)
    for(const [workspaceId, resourceItems] of newResourceItemsByWorkspaceIdMap.entries()) {
      if(!resourceItems) {
        continue
      }
      const updatedResourceItem = resourceItems.find(resourceItem => {
        return resourceItem.resourceEntity.resourceType === resourceType
          && resourceItem.resourceEntity.resourceId === resourceId
      })
      if(!updatedResourceItem) {
        continue
      }
      const oldPosition = updatedResourceItem.favouritePosition ?? 0
      newResourceItemsByWorkspaceIdMap.set(workspaceId, resourceItems.map(resourceItem => {
        if(resourceItem === updatedResourceItem) {
          const newItem = {
            ...resourceItem,
          }
          if (position !== undefined) {
            newItem.favouritePosition = position
          }
          if (color !== undefined) {
            newItem.favouriteColor = color
          }
          return newItem
        }
        if(position === undefined || !resourceItem.isFavouriteToRequestingEntity) {
          return resourceItem
        }
        const favouritePosition = resourceItem.favouritePosition ?? 0
        if(position > oldPosition && favouritePosition > oldPosition && favouritePosition <= position) {
          return {
            ...resourceItem,
            favouritePosition: favouritePosition - 1,
          }
        }
        if(position < oldPosition && favouritePosition >= position && favouritePosition < oldPosition) {
          return {
            ...resourceItem,
            favouritePosition: favouritePosition + 1,
          }
        }
        return resourceItem
      }))
    }
    resourceItemsByWorkspaceIdMap = newResourceItemsByWorkspaceIdMap
    try {
      await resourceItemApiStore.updateEntityResourceItem(resourceType, resourceId, position, color)
    } catch(error) {
      const workspaceId = workspaceStore.currentWorkspace?.id
      if(workspaceId) {
        await fetchResourceItems(workspaceId)
      }
      throw error
    }
  }

  async function updateResourceItemName(resourceItemId: string, name: string) {
    await resourceItemApiStore.updateResourceItemName(resourceItemId, name)
    const newResourceItemsByWorkspaceIdMap = new Map(resourceItemsByWorkspaceIdMap)
    for(const [workspaceId, resourceItems] of newResourceItemsByWorkspaceIdMap.entries()) {
      if(!resourceItems) {
        continue
      }
      const updatedResourceItems = resourceItems.map(resourceItem => {
        if(resourceItem.id === resourceItemId) {
          return {
            ...resourceItem,
            name,
          }
        }
        return resourceItem
      })
      newResourceItemsByWorkspaceIdMap.set(workspaceId, updatedResourceItems)
    }
    resourceItemsByWorkspaceIdMap = newResourceItemsByWorkspaceIdMap
  }

  async function updateResourceItemIcon(resourceItemId: string, iconNameType: IconNameType | null) {
    await resourceItemApiStore.updateResourceItemIcon(resourceItemId, iconNameType)
    const newResourceItemsByWorkspaceIdMap = new Map(resourceItemsByWorkspaceIdMap)
    for(const [workspaceId, resourceItems] of newResourceItemsByWorkspaceIdMap.entries()) {
      if(!resourceItems) {
        continue
      }
      newResourceItemsByWorkspaceIdMap.set(workspaceId, resourceItems.map(resourceItem => {
        if(resourceItem.id === resourceItemId) {
          return {
            ...resourceItem,
            iconNameType: iconNameType || null,
          }
        }
        return resourceItem
      }))
    }
    resourceItemsByWorkspaceIdMap = newResourceItemsByWorkspaceIdMap
  }

  function updateResourceItemIconNameType(resourceType: ResourceType, resourceId: string, iconNameType: IconNameType | null) {
    const newResourceItemsByWorkspaceIdMap = new Map(resourceItemsByWorkspaceIdMap)
    for(const [workspaceId, resourceItems] of newResourceItemsByWorkspaceIdMap.entries()) {
      if(!resourceItems) {
        continue
      }
      newResourceItemsByWorkspaceIdMap.set(workspaceId, resourceItems.map(resourceItem => {
        if(resourceItem.resourceEntity.resourceType === resourceType &&
          resourceItem.resourceEntity.resourceId === resourceId) {
          return {
            ...resourceItem,
            iconNameType: iconNameType || null,
          }
        }
        return resourceItem
      }))
    }
    resourceItemsByWorkspaceIdMap = newResourceItemsByWorkspaceIdMap
  }
  function setResourceItemAccessSummaries(entries: {
    resourceType: ResourceType
    resourceId: string
    accessSummary: IResourceAccessSummary | null
  }[]) {
    if(!entries.length) {
      return
    }
    const accessSummaryByResourceKey = new Map(entries.map(entry => {
      return [getResourceKey(entry.resourceType, entry.resourceId), entry.accessSummary] as const
    }))
    const newResourceItemsByWorkspaceIdMap = new Map(resourceItemsByWorkspaceIdMap)
    for(const [workspaceId, resourceItems] of newResourceItemsByWorkspaceIdMap.entries()) {
      if(!resourceItems) {
        continue
      }
      newResourceItemsByWorkspaceIdMap.set(workspaceId, resourceItems.map(resourceItem => {
        const resourceKey = getResourceKey(resourceItem.resourceEntity.resourceType, resourceItem.resourceEntity.resourceId)
        if(!accessSummaryByResourceKey.has(resourceKey)) {
          return resourceItem
        }
        return {
          ...resourceItem,
          accessSummary: accessSummaryByResourceKey.get(resourceKey),
        }
      }))
    }
    resourceItemsByWorkspaceIdMap = newResourceItemsByWorkspaceIdMap
  }
  function subtractUnseenNotificationCountByResource(resourceType: ResourceType, resourceId: string) {
    const newResourceItemsByWorkspaceIdMap = new Map(resourceItemsByWorkspaceIdMap)
    for (const [workspaceId, resourceItems] of newResourceItemsByWorkspaceIdMap.entries()) {
      newResourceItemsByWorkspaceIdMap.set(workspaceId, resourceItems.map(resourceItem => {
        if(!resourceId && !resourceType) {
          return {
            ...resourceItem,
            unseenNotificationCount: 0,
          }
        }
        if(resourceItem.resourceEntity.resourceId === resourceId && resourceItem.resourceEntity.resourceType === resourceType) {
          return {
            ...resourceItem,
            unseenNotificationCount: 0,
          }
        }
        return resourceItem
      }))
    }
    resourceItemsByWorkspaceIdMap = newResourceItemsByWorkspaceIdMap
  }

  useActivityItemSocketListener(ActivityItemType.ResourceItemCreated, ({payload, authorEntity, restoredFromActivityItemId}) => {
    const workspaceId = payload.resourceItem.workspaceId
    const newResourceItemsByWorkspaceIdMap = new Map(resourceItemsByWorkspaceIdMap)
    if(!newResourceItemsByWorkspaceIdMap.has(workspaceId)) {
      return
    }
    const resourceItems = newResourceItemsByWorkspaceIdMap.get(workspaceId)
    const mappedResourceItem = mapResourceItemResponseInstance(payload.resourceItem)
    const isAlreadyListed = resourceItems.some(resourceItem => resourceItem.id === mappedResourceItem.id)
    const newResourceItems = isAlreadyListed
      ? resourceItems.map(resourceItem => resourceItem.id === mappedResourceItem.id ? mappedResourceItem : resourceItem)
      : [...resourceItems, mappedResourceItem]
    fetchMissingResourceGroup(workspaceId, mappedResourceItem.resourceGroupId)
    const isAuthorCurrentEntity = authorEntity?.entityType === authStore.currentEntity?.entityType
      && authorEntity?.entityId === authStore.currentEntity?.entityId
    if(isAuthorCurrentEntity && !restoredFromActivityItemId && !isImportingTemplate.value && !isAlreadyListed) {
      lastCreatedResourceItem = mappedResourceItem
    }
    newResourceItemsByWorkspaceIdMap.set(workspaceId, newResourceItems)
    resourceItemsByWorkspaceIdMap = newResourceItemsByWorkspaceIdMap
  }, null, {
    lifecycleFn() {
      return hasInitialized
    },
  })

  useActivityItemSocketListener(ActivityItemType.ResourceItemRenamed, ({payload}) => {
    const newResourceItemsByWorkspaceIdMap = new Map(resourceItemsByWorkspaceIdMap)
    for (const [workspaceId, resourceItems] of newResourceItemsByWorkspaceIdMap.entries()) {
      newResourceItemsByWorkspaceIdMap.set(workspaceId, resourceItems.map(resourceItem => {
        if(resourceItem.id === payload.resourceItemId) {
          return {
            ...resourceItem,
            name: payload.name,
          }
        }
        return resourceItem
      }))
    }
    resourceItemsByWorkspaceIdMap = newResourceItemsByWorkspaceIdMap
  }, null, {
    lifecycleFn() {
      return hasInitialized
    },
  })

  useActivityItemSocketListener(ActivityItemType.DatabaseViewUpdated, ({payload, targetResource}) => {
    const resourceItemPatch = getDatabaseViewResourceItemPatch(payload.updatedKeys || [], payload.currentDatabaseView)
    if(!Object.keys(resourceItemPatch).length) {
      return
    }
    let hasMatchedResourceItem = false
    const newResourceItemsByWorkspaceIdMap = new Map(resourceItemsByWorkspaceIdMap)
    for (const [workspaceId, resourceItems] of newResourceItemsByWorkspaceIdMap.entries()) {
      newResourceItemsByWorkspaceIdMap.set(workspaceId, resourceItems.map(resourceItem => {
        if(resourceItem.resourceEntity.resourceType === ResourceType.DatabaseView &&
          resourceItem.resourceEntity.resourceId === targetResource.resourceId) {
          hasMatchedResourceItem = true
          return {
            ...resourceItem,
            ...resourceItemPatch,
          }
        }
        return resourceItem
      }))
    }
    if(!hasMatchedResourceItem) {
      return
    }
    resourceItemsByWorkspaceIdMap = newResourceItemsByWorkspaceIdMap
  }, null, {
    lifecycleFn() {
      return hasInitialized
    },
  })

  useActivityItemSocketListener(ActivityItemType.ResourceItemIconUpdated, ({payload}) => {
    const newResourceItemsByWorkspaceIdMap = new Map(resourceItemsByWorkspaceIdMap)
    for (const [workspaceId, resourceItems] of newResourceItemsByWorkspaceIdMap.entries()) {
      newResourceItemsByWorkspaceIdMap.set(workspaceId, resourceItems.map(resourceItem => {
        if(resourceItem.id === payload.resourceItemId) {
          return {
            ...resourceItem,
            iconNameType: payload.iconNameType || null,
          }
        }
        return resourceItem
      }))
    }
    resourceItemsByWorkspaceIdMap = newResourceItemsByWorkspaceIdMap
  }, null, {
    lifecycleFn() {
      return hasInitialized
    },
  })

  useActivityItemSocketListener(ActivityItemType.AutomationUpdated, ({payload}) => {
    const currentAutomation = mapAutomationResponseInstance(payload.currentAutomation as IToJson<IAutomationResponseInstance>)
    let hasMatchedResourceItem = false
    const newResourceItemsByWorkspaceIdMap = new Map(resourceItemsByWorkspaceIdMap)
    for (const [workspaceId, resourceItems] of newResourceItemsByWorkspaceIdMap.entries()) {
      newResourceItemsByWorkspaceIdMap.set(workspaceId, resourceItems.map(resourceItem => {
        if(resourceItem.resourceEntity.resourceType === ResourceType.Automation &&
          resourceItem.resourceEntity.resourceId === currentAutomation.id) {
          hasMatchedResourceItem = true
          return {
            ...resourceItem,
            resourceEntity: {
              ...resourceItem.resourceEntity,
              payload: currentAutomation,
            },
          }
        }
        return resourceItem
      }))
    }
    if(!hasMatchedResourceItem) {
      return
    }
    resourceItemsByWorkspaceIdMap = newResourceItemsByWorkspaceIdMap
  }, null, {
    lifecycleFn() {
      return hasInitialized
    },
  })

  useActivityItemSocketListener(ActivityItemType.ResourceItemPayloadUpdated, ({payload}) => {
    const newResourceItemsByWorkspaceIdMap = new Map(resourceItemsByWorkspaceIdMap)
    for (const [workspaceId, resourceItems] of newResourceItemsByWorkspaceIdMap.entries()) {
      newResourceItemsByWorkspaceIdMap.set(workspaceId, resourceItems.map(resourceItem => {
        if(resourceItem.id === payload.resourceItemId) {
          return {
            ...resourceItem,
            resourcePayload: payload.resourcePayload,
            thumbnailPayload: payload.thumbnailPayload ?? resourceItem.thumbnailPayload,
          }
        }
        return resourceItem
      }))
    }
    resourceItemsByWorkspaceIdMap = newResourceItemsByWorkspaceIdMap
  }, null, {
    lifecycleFn() {
      return hasInitialized
    },
  })

  useActivityItemSocketListener(ActivityItemType.ResourceItemDeleted, ({payload, authorEntity}) => {
    const newResourceItemsByWorkspaceIdMap = new Map(resourceItemsByWorkspaceIdMap)
    let deletedResourceItem: IResourceItemResponseInstance = null
    for (const [workspaceId, resourceItems] of newResourceItemsByWorkspaceIdMap.entries()) {
      newResourceItemsByWorkspaceIdMap.set(workspaceId, resourceItems.filter(resourceItem => {
        if(resourceItem.id === payload.resourceItemId) {
          deletedResourceItem = resourceItem
          return false
        }
        return true
      }))
    }
    resourceItemsByWorkspaceIdMap = newResourceItemsByWorkspaceIdMap
    const route = router.currentRoute.value
    const currentRouteResource = route.meta.determineResource?.(route) ?? null
    if(!currentRouteResource || currentRouteResource.resourceType !== payload.resourceType || currentRouteResource.resourceId !== payload.resourceId) {
      return
    }

    const workspaceName = route.params?.workspaceName as string | undefined
    if(!workspaceName) {
      return
    }
    const currentEntity = authStore.currentEntity
    const isAuthorCurrentEntity = !!authorEntity && !!currentEntity
      && authorEntity.entityType === currentEntity.entityType
      && authorEntity.entityId === currentEntity.entityId
    // someone else's change, not a failure of the user's own action
    if(!isAuthorCurrentEntity) {
      messages.info(deletedResourceItem?.name
        ? i18n.global.t('workspaces.resourceHasBeenDeleted', {name: deletedResourceItem.name})
        : i18n.global.t('workspaces.resourceHasBeenDeletedGeneric'))
    }
    router.push({
      name: RouteName.WorkspaceHome,
      params: {
        workspaceName,
      },
    })
  }, null, {
    lifecycleFn() {
      return hasInitialized
    },
  })

  useSocketListener<IResourceAccessRevokedEmitPayload>(GlobalSocketEventType.ResourceAccessRevoked, payload => {
    const revokedAt = Date.now()
    const revokedResourceKeys = new Set<string>([getResourceKey(payload.resourceType, payload.resourceId)])
    const newResourceItemsByWorkspaceIdMap = new Map(resourceItemsByWorkspaceIdMap)
    for (const [workspaceId, resourceItems] of newResourceItemsByWorkspaceIdMap.entries()) {
      newResourceItemsByWorkspaceIdMap.set(workspaceId, resourceItems.filter(resourceItem => {
        const isRevoked = resourceItem.resourcePath?.some(resourcePathItem => {
          return resourcePathItem.resourceType === payload.resourceType
            && String(resourcePathItem.resourceId) === String(payload.resourceId)
        })
        if(!isRevoked) {
          return true
        }
        revokedResourceKeys.add(getResourceKey(
          resourceItem.resourceEntity.resourceType,
          String(resourceItem.resourceEntity.resourceId),
        ))
        return false
      }))
    }
    resourceItemsByWorkspaceIdMap = newResourceItemsByWorkspaceIdMap
    for(const revokedResourceKey of revokedResourceKeys) {
      revokedAtByResourceKey.set(revokedResourceKey, revokedAt)
      cacheStore.setCacheItem(`permissions-${revokedResourceKey}`, undefined)
    }
    if(payload.resourceType === ResourceType.Workspace) {
      return
    }
    const route = router.currentRoute.value
    if(!isRouteOnRevokedResource(route, revokedResourceKeys)) {
      return
    }
    const workspaceName = route.params?.workspaceName as string | undefined
    if(!workspaceName) {
      return
    }
    messages.info(i18n.global.t('workspaces.resourceAccessHasBeenRevoked'))
    router.push({
      name: RouteName.WorkspaceHome,
      params: {
        workspaceName,
      },
    })
  }, {
    lifecycleFn() {
      return hasInitialized
    },
  })

  useActivityItemSocketListener(ActivityItemType.ResourceItemPositionUpdated, ({payload, authorEntity}) => {
    if(authStore.currentEntity?.entityId === authorEntity.entityId && authStore.currentEntity?.entityType === authorEntity.entityType) {
      return
    }
    const newResourceItemsByWorkspaceIdMap = new Map(resourceItemsByWorkspaceIdMap)
    for (const [workspaceId, resourceItems] of newResourceItemsByWorkspaceIdMap.entries()) {
      newResourceItemsByWorkspaceIdMap.set(workspaceId, resourceItems.map(resourceItem => {
        if(resourceItem.id === payload.resourceItemId) {
          return {
            ...resourceItem,
            position: payload.position,
            resourceGroupId: payload.resourceGroupId,
            resourceSpaceId: payload.resourceSpaceId,
          }
        }
        return resourceItem
      }))
    }
    resourceItemsByWorkspaceIdMap = newResourceItemsByWorkspaceIdMap
  }, null, {
    lifecycleFn() {
      return hasInitialized
    },
  })

  useActivityItemSocketListener(ActivityItemType.ResourceGroupPositionUpdated, ({payload, authorEntity}) => {
    if(authStore.currentEntity?.entityId === authorEntity.entityId && authStore.currentEntity?.entityType === authorEntity.entityType) {
      return
    }
    const newResourceGroupsByWorkspaceIdMap = new Map(resourceGroupsByWorkspaceIdMap)
    for (const [workspaceId, resourceGroups] of newResourceGroupsByWorkspaceIdMap.entries()) {
      newResourceGroupsByWorkspaceIdMap.set(workspaceId, resourceGroups.map(resourceGroup => {
        if(resourceGroup.id === payload.resourceGroupId) {
          return {
            ...resourceGroup,
            position: payload.position,
            resourceSpaceId: payload.resourceSpaceId,
          }
        }
        return resourceGroup
      }))
    }
    resourceGroupsByWorkspaceIdMap = newResourceGroupsByWorkspaceIdMap
  }, null, {
    lifecycleFn() {
      return hasInitialized
    },
  })

  useSocketListener<IResourceSpaceDeletedEmitPayload>(GlobalSocketEventType.ResourceSpaceDeleted, payload => {
    const {workspaceId, resourceSpaceId, destinationResourceSpaceId} = payload
    const moveOutOfDeletedSpace = <T extends {resourceSpaceId: string}>(entities: T[]): T[] => {
      if(!destinationResourceSpaceId) {
        return entities.filter(entity => entity.resourceSpaceId !== resourceSpaceId)
      }
      return entities.map(entity => {
        if(entity.resourceSpaceId !== resourceSpaceId) {
          return entity
        }
        return {
          ...entity,
          resourceSpaceId: destinationResourceSpaceId,
        }
      })
    }
    const resourceItems = resourceItemsByWorkspaceIdMap.get(workspaceId)
    if(resourceItems) {
      const newResourceItemsByWorkspaceIdMap = new Map(resourceItemsByWorkspaceIdMap)
      newResourceItemsByWorkspaceIdMap.set(workspaceId, moveOutOfDeletedSpace(resourceItems))
      resourceItemsByWorkspaceIdMap = newResourceItemsByWorkspaceIdMap
    }
    const resourceGroups = resourceGroupsByWorkspaceIdMap.get(workspaceId)
    if(resourceGroups) {
      const newResourceGroupsByWorkspaceIdMap = new Map(resourceGroupsByWorkspaceIdMap)
      newResourceGroupsByWorkspaceIdMap.set(workspaceId, moveOutOfDeletedSpace(resourceGroups))
      resourceGroupsByWorkspaceIdMap = newResourceGroupsByWorkspaceIdMap
    }
  }, {
    lifecycleFn() {
      return hasInitialized
    },
  })

  useSocketListener<IResourceLastActivityChangeEmitPayload>(GlobalSocketEventType.ResourceLastActivityChange, payload => {
    const newResourceItemsByWorkspaceIdMap = new Map(resourceItemsByWorkspaceIdMap)
    for (const [workspaceId, resourceItems] of newResourceItemsByWorkspaceIdMap.entries()) {
      newResourceItemsByWorkspaceIdMap.set(workspaceId, resourceItems.map(resourceItem => {
        if(resourceItem.resourceEntity.resourceId === payload.resourceId && resourceItem.resourceEntity.resourceType === payload.resourceType) {
          const isCurrentUserAuthor = payload.authorEntity && authStore.currentEntity?.entityId === payload.authorEntity.entityId && authStore.currentEntity?.entityType === payload.authorEntity.entityType
          return {
            ...resourceItem,
            lastActivityAt: new Date(payload.lastActivityAt),
            ...(isCurrentUserAuthor ? {readAt: new Date(payload.lastActivityAt)} : {}),
          }
        }
        return resourceItem
      }))
    }
    resourceItemsByWorkspaceIdMap = newResourceItemsByWorkspaceIdMap
  }, {
    lifecycleFn() {
      return hasInitialized
    },
  })

  useSocketListener<IResourceReadAtChangeEmitPayload>(GlobalSocketEventType.ResourceReadAtChange, payload => {
    const newResourceItemsByWorkspaceIdMap = new Map(resourceItemsByWorkspaceIdMap)
    for (const [workspaceId, resourceItems] of newResourceItemsByWorkspaceIdMap.entries()) {
      newResourceItemsByWorkspaceIdMap.set(workspaceId, resourceItems.map(resourceItem => {
        if(resourceItem.resourceEntity.resourceId !== payload.resourceId || 
           resourceItem.resourceEntity.resourceType !== payload.resourceType) {
          return resourceItem
        }
        return {
          ...resourceItem,
          readAt: payload.readAt ? new Date(payload.readAt) : null,
        }
      }))
    }
    resourceItemsByWorkspaceIdMap = newResourceItemsByWorkspaceIdMap
  }, {
    lifecycleFn() {
      return hasInitialized
    },
  })

  useSocketListener<INotificationCreatedEmitPayload>(GlobalSocketEventType.NotificationCreated, (payload) => {
    const notification = payload.notification
    if(notification.isTransient || notificationCountsStore.isNotificationSeenLive(notification)) {
      return
    }
    // A count above 1 means the event folded into an already-unread group head — the server row
    // count did not grow, and the seen subtraction below works in row counts, so incrementing per
    // event would leave the badge permanently above what a refetch shows
    if((notification.unreadGroupCount ?? 1) > 1) {
      return
    }
    const newResourceItemsByWorkspaceIdMap = new Map(resourceItemsByWorkspaceIdMap)
    for (const [workspaceId, resourceItems] of newResourceItemsByWorkspaceIdMap.entries()) {
      newResourceItemsByWorkspaceIdMap.set(workspaceId, resourceItems.map(resourceItem => {
        const isNotificationAboutResourceItem = notification.resourcePath.some(resourcePathItem => {
          return resourcePathItem.resourceId === resourceItem.resourceEntity.resourceId &&
            resourcePathItem.resourceType === resourceItem.resourceEntity.resourceType
        })
        if(!isNotificationAboutResourceItem) {
          return resourceItem
        }
        return {
          ...resourceItem,
          unseenNotificationCount: resourceItem.unseenNotificationCount + 1,
        }
      }))
    }
    resourceItemsByWorkspaceIdMap = newResourceItemsByWorkspaceIdMap
  }, {
    lifecycleFn() {
      return hasInitialized
    },
  })

  function subtractSeenNotificationsFromUnseenCounts(seenNotifications: INotificationResourcePathEmitItem[]) {
    const countedSeenNotifications = seenNotifications.filter(notification => {
      return !notificationCountsStore.wasNotificationSeenLive(notification.id)
    })
    if(!countedSeenNotifications.length) {
      return
    }
    const newResourceItemsByWorkspaceIdMap = new Map(resourceItemsByWorkspaceIdMap)
    for (const [workspaceId, resourceItems] of newResourceItemsByWorkspaceIdMap.entries()) {
      newResourceItemsByWorkspaceIdMap.set(workspaceId, resourceItems.map(resourceItem => {
        const seenCount = countedSeenNotifications.filter(notification => {
          return notification.resourcePath.some(resourcePathItem => {
            return resourcePathItem.resourceId === resourceItem.resourceEntity.resourceId &&
              resourcePathItem.resourceType === resourceItem.resourceEntity.resourceType
          })
        }).length
        if(!seenCount) {
          return resourceItem
        }
        return {
          ...resourceItem,
          unseenNotificationCount: Math.max(0, resourceItem.unseenNotificationCount - seenCount),
        }
      }))
    }
    resourceItemsByWorkspaceIdMap = newResourceItemsByWorkspaceIdMap
  }

  useSocketListener<INotificationsSeenEmitPayload>(GlobalSocketEventType.NotificationsSeen, (payload) => {
    if(payload.notifications) {
      subtractSeenNotificationsFromUnseenCounts(payload.notifications)
      return
    }
    const newResourceItemsByWorkspaceIdMap = new Map(resourceItemsByWorkspaceIdMap)
    for (const [workspaceId, resourceItems] of newResourceItemsByWorkspaceIdMap.entries()) {
      newResourceItemsByWorkspaceIdMap.set(workspaceId, resourceItems.map(resourceItem => {
        if(!payload.resourceId && !payload.resourceType) {
          return {
            ...resourceItem,
            unseenNotificationCount: 0,
          }
        }
        if(resourceItem.resourceEntity.resourceId === payload.resourceId && resourceItem.resourceEntity.resourceType === payload.resourceType) {
          return {
            ...resourceItem,
            unseenNotificationCount: Math.max(0, resourceItem.unseenNotificationCount - payload.count),
          }
        }
        return resourceItem
      }))
    }
    resourceItemsByWorkspaceIdMap = newResourceItemsByWorkspaceIdMap
  }, {
    lifecycleFn() {
      return hasInitialized
    },
  })

  useSocketListener<InotificationReadEventPayload>(GlobalSocketEventType.NotificationRead, (payload) => {
    const notification = payload.notification
    const newResourceItemsByWorkspaceIdMap = new Map(resourceItemsByWorkspaceIdMap)
    for (const [workspaceId, resourceItems] of newResourceItemsByWorkspaceIdMap.entries()) {
      newResourceItemsByWorkspaceIdMap.set(workspaceId, resourceItems.map(resourceItem => {
        const isNotificationAboutResourceItem = notification.resourcePath.some(resourcePathItem => {
          return resourcePathItem.resourceId === resourceItem.resourceEntity.resourceId &&
            resourcePathItem.resourceType === resourceItem.resourceEntity.resourceType
        })
        if(!isNotificationAboutResourceItem) {
          return resourceItem
        }
        return {
          ...resourceItem,
          unseenNotificationCount: Math.max(0, resourceItem.unseenNotificationCount - 1),
        }
      }))
    }
    resourceItemsByWorkspaceIdMap = newResourceItemsByWorkspaceIdMap
  }, {
    lifecycleFn() {
      return hasInitialized
    },
  })

  useSocketListener<INotificationsReadEmitPayload>(GlobalSocketEventType.NotificationsRead, (payload) => {
    if(payload.notificationIds) {
      subtractSeenNotificationsFromUnseenCounts(payload.seenNotifications ?? [])
      return
    }
    const newResourceItemsByWorkspaceIdMap = new Map(resourceItemsByWorkspaceIdMap)
    for (const [workspaceId, resourceItems] of newResourceItemsByWorkspaceIdMap.entries()) {
      newResourceItemsByWorkspaceIdMap.set(workspaceId, resourceItems.map(resourceItem => {
        if(!payload.resourceId && !payload.resourceType) {
          return {
            ...resourceItem,
            unseenNotificationCount: 0,
          }
        }
        if(resourceItem.resourceEntity.resourceId === payload.resourceId && resourceItem.resourceEntity.resourceType === payload.resourceType) {
          return {
            ...resourceItem,
            unseenNotificationCount: 0,
          }
        }
        return resourceItem
      }))
    }
    resourceItemsByWorkspaceIdMap = newResourceItemsByWorkspaceIdMap
  }, {
    lifecycleFn() {
      return hasInitialized
    },
  })

  return $$({
    resourceItems,
    sortedResourceItems,
    resourceGroups,
    hasLoaded,
    lastCreatedResourceItem,
    setIsImportingTemplate,
    setResourceGroups,
    setResourceItems,
    hasAnyResource,
    initialize,
    dispose,
    fetchResourceGroups,
    fetchResourceItems,
    markResourceAsRead,
    markResourceAsUnread,
    subtractUnseenNotificationCountByResource,
    updateLocalResouceItems,
    toggleResourceItemFavourite,
    updateResourceItemIconNameType,
    setResourceItemAccessSummaries,
    updateResourceItemIcon,
    updateEntityResourceItem,
    updateResourceItemName,
  })
})
